/**
 * Gender, matching the backend's `com.akshar.spectrum.common.dto.Gender`.
 *
 * The columns behind this are free-text VARCHARs, so a stored value can be any
 * spelling a school has ever typed — `MALE`, `male`, `M`, `Boy`. The API now
 * canonicalises on write and a migration cleans the rows already on disk, but
 * a picker cannot assume that has happened: a `<select>` whose options are
 * `MALE` will not select a value of `Male`, so it renders as "not set" for a
 * student who has a gender recorded — and saving that section then sends a
 * blank, which the API reads as a deliberate clear. That is how a display
 * mismatch turns into data loss.
 *
 * So: build every picker from {@link GENDER_OPTIONS}, and put whatever the API
 * returned through {@link canonicalGender} before seeding the form.
 */

/** The canonical values — identical to the enum constants the API stores. */
export const GENDER_OPTIONS: readonly string[] = ['Male', 'Female', 'Others'];

/*
 * Keyed on the normalised form (letters only, upper case) so "M.", "male " and
 * "Male" all arrive at the same entry. Mirrors the synonym lists in Gender.java
 * — add to both when a new spelling turns up.
 */
const BY_TOKEN: Readonly<Record<string, string>> = {
    MALE: 'Male', M: 'Male', BOY: 'Male', BOYS: 'Male', MAN: 'Male', MEN: 'Male',
    SON: 'Male', LADKA: 'Male', BALAK: 'Male',

    FEMALE: 'Female', F: 'Female', GIRL: 'Female', GIRLS: 'Female', WOMAN: 'Female',
    WOMEN: 'Female', DAUGHTER: 'Female', LADKI: 'Female', BALIKA: 'Female',

    OTHERS: 'Others', OTHER: 'Others', O: 'Others', TRANSGENDER: 'Others',
    TRANS: 'Others', THIRDGENDER: 'Others', NONBINARY: 'Others', NB: 'Others',
    INTERSEX: 'Others', PREFERNOTTOSAY: 'Others'
};

/**
 * Values meaning "nobody filled this in". Deliberately not folded into
 * `Others` — unrecorded is not a gender, and treating it as one would show a
 * student as Others when nothing was ever collected.
 */
const BLANK_TOKENS = new Set([
    'NA', 'NOTAVAILABLE', 'NOTSPECIFIED', 'NOTAPPLICABLE', 'UNSPECIFIED',
    'UNKNOWN', 'NONE', 'NULL', 'NIL', 'BLANK', 'EMPTY'
]);

/**
 * Maps any stored spelling onto the canonical option, or `''` when the value
 * is empty, a "not recorded" placeholder, or a spelling nobody has taught this
 * about. Returning `''` rather than the raw input matters: an unmatched value
 * left in a form control would be submitted back and re-dirty the column.
 */
export function canonicalGender(value: string | null | undefined): string {
    const token = (value ?? '').replace(/[^a-zA-Z]/g, '').toUpperCase();
    if (!token || BLANK_TOKENS.has(token)) {
        return '';
    }
    return BY_TOKEN[token] ?? '';
}
